import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import IdleTimer from 'react-idle-timer'
import { useInterval, useCompareToPrev, useBeforeUnload } from 'utils/hooks'
import {
  LOCAL_STORAGE_KEYS,
  MAX_TIMEOUT_SECONDS,
  DEFAULT_TIMEOUT_SECONDS,
  MS_IN_SECOND
} from 'utils/constants'
import { replaceInString } from 'utils/misc'

const isPastTimeLimit = (timerRef, timeoutSeconds) => {
  const now = Date.now()
  const lastActiveMs = timerRef.current?.getLastActiveTime()
  const totalIdleMs = now - (lastActiveMs || now)
  const forcedTimeoutMs =
    (timeoutSeconds + DEFAULT_TIMEOUT_SECONDS) * MS_IN_SECOND
  return totalIdleMs > forcedTimeoutMs
}

const ModalContent = ({
  closeModal,
  modal,
  onLogout,
  strings,
  timerRef,
  finalTimeoutSeconds
}) => {
  const [remaining, setRemaining] = useState(DEFAULT_TIMEOUT_SECONDS)
  const hasModalStateChanged = useCompareToPrev(modal.open)
  const initialInactiveTimeMs = useRef(timerRef.current?.getLastActiveTime())

  useEffect(() => {
    if (hasModalStateChanged) {
      if (!modal.open) setRemaining(DEFAULT_TIMEOUT_SECONDS)
    }
  })

  useInterval(() => {
    if (isPastTimeLimit(timerRef, finalTimeoutSeconds)) {
      onLogout()
      closeModal?.()
    } else {
      const now = Date.now()
      const inactiveSeconds = Math.round(
        (now - initialInactiveTimeMs.current) / 1000
      )
      let secondsLeft = finalTimeoutSeconds - inactiveSeconds
      if (secondsLeft < 1) secondsLeft = 1
      setRemaining(secondsLeft)
      if (remaining === 1) {
        onLogout()
      }
    }
  }, MS_IN_SECOND)
  return replaceInString(strings.inactive_desc, '[##]', remaining)
}

const SessionTimeout = props => {
  const {
    onLogout,
    openModal,
    sessionTimeoutSeconds,
    strings,
    humiIsabellaDisabled
  } = props
  const timerRef = useRef()

  const finalTimeoutSeconds =
    sessionTimeoutSeconds < MAX_TIMEOUT_SECONDS
      ? sessionTimeoutSeconds
      : MAX_TIMEOUT_SECONDS
  const timeoutMs =
    (finalTimeoutSeconds - DEFAULT_TIMEOUT_SECONDS) * MS_IN_SECOND

  const onIdle = () => {
    if (isPastTimeLimit(timerRef, finalTimeoutSeconds)) {
      onLogout()
    } else {
      openModal({
        className: 'timeout',
        heading: strings.inactive_title,
        children: (
          <ModalContent
            {...props}
            timerRef={timerRef}
            finalTimeoutSeconds={finalTimeoutSeconds}
          />
        ),
        confirmButton: strings.inactive_return,
        cancelButton: strings.sign_out,
        closeOnBackgroundClick: true,
        humi: !humiIsabellaDisabled,
        onConfirm: timerRef.current.reset,
        onCancel: onLogout,
        onBackgroundClick: () => {}
      })
    }
  }

  useBeforeUnload(() => {
    // Remember epoch time when window is closed
    const localUserData = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.user)
    )
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.user,
      JSON.stringify({
        ...localUserData,
        last_active: Date.now().toString()
      })
    )
  })

  useEffect(() => {
    const localUserData = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.user)
    )
    if (localUserData?.last_active) {
      const now = Date.now()
      const elapsedSeconds = (now - Number(localUserData.last_active)) / 1000
      // If the last active user closed this site
      // longer than [timeout seconds] ago, logout
      if (elapsedSeconds >= finalTimeoutSeconds) {
        onLogout()
        return
      }
      delete localUserData.last_active
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.user,
        JSON.stringify(localUserData)
      )
    }
  }, [])

  return (
    <IdleTimer
      ref={timerRef}
      onIdle={onIdle}
      debounce={250}
      timeout={timeoutMs > 0 ? timeoutMs : 0}
    />
  )
}

ModalContent.propTypes = {
  isModalOpen: PropTypes.bool,
  onLogout: PropTypes.func
}

SessionTimeout.propTypes = {
  modal: PropTypes.object,
  onLogout: PropTypes.func,
  openModal: PropTypes.func,
  sessionTimeoutSeconds: PropTypes.number,
  strings: PropTypes.objectOf(PropTypes.string)
}

export default SessionTimeout
